import type { CourierSettlementLinesFilters } from '../../../models/settlementLinesFilters';
import {
  isSettlementLinesExportNotFoundError,
  useCourierSettlementLinesExport,
} from '../../hooks/useCourierSettlementLinesExport';
import { Button } from '@shared/components/ui/button';
import { faFileArrowDown, faSpinnerThird } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from '@shared/hooks/useToast';
import { useTranslation } from 'react-i18next';

interface SettlementDownloadExcelButtonProps {
  settlementId: number;
  filters: CourierSettlementLinesFilters;
}

/**
 * Mismo aspecto que Orders `DownloadExcelButton` (outline + icono descarga / spinner).
 */
export const SettlementDownloadExcelButton = ({ settlementId, filters }: SettlementDownloadExcelButtonProps) => {
  const { t } = useTranslation('courierInvoicesModule');
  const { toast } = useToast();
  const { mutate, isPending } = useCourierSettlementLinesExport(settlementId);

  const handleExport = () => {
    mutate(filters, {
      onSuccess: () => {
        toast({
          title: t('settlement.lines_filters.export_success_title'),
          description: t('settlement.lines_filters.export_success_description'),
          variant: 'default',
        });
      },
      onError: (err: unknown) => {
        if (isSettlementLinesExportNotFoundError(err)) {
          toast({
            title: t('settlement.lines_filters.export_empty_title'),
            description: t('settlement.lines_filters.export_empty_description'),
            variant: 'destructive',
          });
        } else {
          const msg =
            err &&
            typeof err === 'object' &&
            'message' in err &&
            typeof (err as { message?: string }).message === 'string'
              ? (err as { message: string }).message
              : t('errors.unexpected');
          toast({
            title: t('settlement.lines_filters.export_error_title'),
            description: msg,
            variant: 'destructive',
          });
        }
      },
    });
  };

  return (
    <Button
      onClick={handleExport}
      disabled={isPending}
      type="button"
      size="sm"
      variant="outline"
      className="!py-[9px] !px-2 text-foreground/70 hover:text-foreground border-input hover:bg-accent"
      data-testid="settlement-lines-export-excel"
      title={t('settlement.lines_filters.export')}
    >
      {isPending ? (
        <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin" />
      ) : (
        <FontAwesomeIcon icon={faFileArrowDown} />
      )}
    </Button>
  );
};
